import { createApolloClient } from "../../services/apolloClient";
import {
  Order,
  CreateOrderInput,
  OrderStatus,
  OrderResponse,
  CustomerOrdersResponse,
  CreateOrderResponse,
  AvailableTimeSlot,
} from "./types";
import {
  ORDER_QUERY,
  CUSTOMER_ORDERS_QUERY,
  CREATE_ORDER_MUTATION,
  APPROVE_ORDER_MUTATION,
  CONFIRM_ORDER_PAYMENT_MUTATION,
  REPORT_ORDER_ISSUE_MUTATION,
  SET_DELIVERY_SLOT_MUTATION,
  AVAILABLE_TIME_SLOTS_QUERY,
} from "./mutations";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ message?: string }> }
  ).graphQLErrors;

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const firstMessage = graphQLErrors[0]?.message;
    if (firstMessage) {
      return firstMessage.replace(/^Error:\s*/i, "");
    }
  }

  if (error instanceof Error && error.message) {
    return error.message.replace(/^Error:\s*/i, "");
  }

  return fallback;
};

const createClient = () => createApolloClient(true);

const mutateOrder = async (
  mutation: any,
  variables: Record<string, unknown>,
  key: string,
  fallback: string,
): Promise<Order> => {
  try {
    const result = await createClient().mutate({ mutation, variables });
    const order = (result.data as Record<string, Order> | undefined)?.[key];
    if (!order) throw new Error(fallback);
    return order;
  } catch (error) {
    throw new Error(getErrorMessage(error, fallback));
  }
};

export const orderService = {
  async getOrder(id: string): Promise<Order> {
    try {
      const result = await createClient().query({
        query: ORDER_QUERY,
        variables: { id },
        fetchPolicy: "network-only",
      });

      const data = result.data as OrderResponse | undefined;

      if (!data?.order) {
        throw new Error("Failed to fetch order");
      }

      return data.order;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch order"));
    }
  },

  async getCustomerOrders(status: OrderStatus): Promise<Order[]> {
    try {
      const result = await createClient().query({
        query: CUSTOMER_ORDERS_QUERY,
        variables: { status },
        fetchPolicy: "network-only",
      });

      const orders = (result.data as CustomerOrdersResponse | undefined)
        ?.customerOrders;

      if (!orders) {
        throw new Error("Failed to fetch customer orders");
      }

      return orders;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, "Failed to fetch customer orders"),
      );
    }
  },

  async createOrder(input: CreateOrderInput): Promise<Order> {
    try {
      const result = await createClient().mutate({
        mutation: CREATE_ORDER_MUTATION,
        variables: input,
      });

      const data = result.data as CreateOrderResponse | undefined;

      if (!data?.createOrder) {
        throw new Error("Failed to create order");
      }

      return data.createOrder;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to create order"));
    }
  },

  approveOrder(id: string): Promise<Order> {
    return mutateOrder(APPROVE_ORDER_MUTATION, { id }, "approveOrder", "Failed to approve order");
  },

  /** Back from SkipCash: the server checks the payment with SkipCash and records it if paid */
  confirmPayment(id: string, paymentId: string | null): Promise<Order> {
    return mutateOrder(
      CONFIRM_ORDER_PAYMENT_MUTATION,
      { id, paymentId },
      "confirmOrderPayment",
      "Failed to check the payment",
    );
  },

  reportOrderIssue(id: string, message: string): Promise<Order> {
    return mutateOrder(
      REPORT_ORDER_ISSUE_MUTATION,
      { id, message },
      "reportOrderIssue",
      "Failed to report the issue",
    );
  },

  setDeliverySlot(id: string, slotId: string, date: string): Promise<Order> {
    return mutateOrder(
      SET_DELIVERY_SLOT_MUTATION,
      { id, slotId, date },
      "setDeliverySlot",
      "Failed to book the delivery slot",
    );
  },

  async getAvailableTimeSlots(
    type: "pickup" | "delivery",
    date: string,
  ): Promise<AvailableTimeSlot[]> {
    try {
      const result = await createClient().query({
        query: AVAILABLE_TIME_SLOTS_QUERY,
        variables: { type, date },
        fetchPolicy: "network-only",
      });
      return (
        (result.data as { availableTimeSlots?: AvailableTimeSlot[] } | undefined)
          ?.availableTimeSlots ?? []
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to load time slots"));
    }
  },
};
